import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, TargetIcon, UsersIcon, LayoutIcon, FileTextIcon, CameraIcon, PaletteIcon, TypeIcon, MailIcon, BookOpenIcon, SearchIcon, LinkIcon, MonitorIcon, SmartphoneIcon, ZapIcon, ServerIcon, ShieldCheckIcon, SettingsIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteLatenMakenChecklistPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken checklist | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Complete checklist voor het laten maken van een website. Van domeinnaam en hosting tot content en SEO. Vergeet niets met deze handige checklist van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website laten maken checklist | Noord',
      'og:description': 'Complete checklist voor het laten maken van een website. Van domeinnaam en hosting tot content en SEO.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-laten-maken-checklist',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe lang duurt het om een website te laten maken?',
    a: "Gemiddeld duurt het 2 tot 4 weken om een professionele website te bouwen. Dit hangt af van de complexiteit, het aantal pagina's en hoe snel je feedback en content aanlevert."
  }, {
    q: 'Kan ik zelf de teksten schrijven?',
    a: 'Ja, dat kan zeker. Wij geven je tips en richtlijnen voor effectieve webteksten. Heb je liever dat wij de teksten schrijven? Dan verzorgen wij professionele copywriting die aansluit bij je doelgroep en SEO.'
  }, {
    q: 'Wat moet er op een homepage staan?',
    a: 'Een goede homepage bevat: een duidelijke kop die vertelt wat je doet, je belangrijkste diensten, social proof (reviews of keurmerken), een duidelijke call-to-action en contactmogelijkheden. De homepage is je digitale visitekaartje.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2149">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2150">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2151">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2152" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2153" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2154" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2155">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2156">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2157">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2158">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2159">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2160">/</span>
            <span className="text-accent-light font-medium" data-id="element-2161">
              Website checklist
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2162">
            Checklist voor een succesvolle website
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2163">
            Alles wat je nodig hebt voordat je een website laat maken. Van
            strategie tot livegang — met deze checklist ben je optimaal
            voorbereid.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2164">
            <a href="#/website-laten-maken" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2165">
              Bekijk onze website diensten
              <ArrowRightIcon className="w-5 h-5" data-id="element-2166" />
            </a>
          </div>
        </div>
      </section>

      {/* Stap 1: Strategie */}
      <section className="py-24 bg-surface-muted" data-id="element-2167">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2168">
          <div className="text-center mb-16" data-id="element-2169">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2170">
              Stap 1
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2171">
              Strategie: bepaal de basis
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2172">
              Voordat je begint met bouwen, is het belangrijk om een duidelijke
              strategie te hebben.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2173">
            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2174">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2175">
                <TargetIcon className="w-6 h-6 text-accent" data-id="element-2176" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2177">
                Doel van de website
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2178">
                Wat wil je bereiken? Meer aanvragen, online verkopen of
                naamsbekendheid? Een helder doel bepaalt de richting van je
                website.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2179">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2180">
                <UsersIcon className="w-6 h-6 text-accent" data-id="element-2181" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2182">
                Doelgroep bepalen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2183">
                Wie zijn je ideale klanten? Hoe beter je je doelgroep kent, hoe
                effectiever je website wordt ingericht.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2184">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2185">
                <LayoutIcon className="w-6 h-6 text-accent" data-id="element-2186" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2187">
                Belangrijkste pagina's
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2188">
                Welke pagina's heeft je website nodig? Denk aan: homepage, over
                ons, diensten, contact en eventueel een blog.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 2: Content */}
      <section className="py-24 bg-white" data-id="element-2189">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2190">
          <div className="text-center mb-16" data-id="element-2191">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2192">
              Stap 2
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2193">
              Content: verzamel je materiaal
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2194">
              Goede content is de basis van een succesvolle website. Bereid dit
              voor.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2195">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2196">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2197">
                <FileTextIcon className="w-6 h-6 text-accent" data-id="element-2198" />
              </div>
              <div data-id="element-2199">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2200">
                  Teksten
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2201">
                  Schrijf heldere teksten die je doelgroep aanspreken. Wij
                  kunnen hierbij helpen met professionele copywriting.
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2202">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2203">
                <CameraIcon className="w-6 h-6 text-accent" data-id="element-2204" />
              </div>
              <div data-id="element-2205">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2206">Foto's</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2207">
                  Professionele foto's maken een groot verschil. Gebruik eigen
                  foto's of kwalitatieve stockfoto's.
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2208">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2209">
                <PaletteIcon className="w-6 h-6 text-accent" data-id="element-2210" />
              </div>
              <div data-id="element-2211">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2212">Logo</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2213">
                  Zorg dat je logo beschikbaar is in hoge kwaliteit (bij
                  voorkeur als vectorbestand).
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2214">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2215">
                <TypeIcon className="w-6 h-6 text-accent" data-id="element-2216" />
              </div>
              <div data-id="element-2217">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2218">
                  Branding
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2219">
                  Heb je een huisstijl? Denk aan kleuren, lettertypen en de
                  uitstraling die je wilt communiceren.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 3: Functionaliteiten */}
      <section className="py-24 bg-surface-muted" data-id="element-2220">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2221">
          <div className="text-center mb-16" data-id="element-2222">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2223">
              Stap 3
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2224">
              Functionaliteiten: wat moet je website kunnen?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2225">
              Bepaal welke functies essentieel zijn voor jouw bedrijf.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2226">
            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2227">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2228">
                <MailIcon className="w-6 h-6 text-accent" data-id="element-2229" />
              </div>
              <div data-id="element-2230">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2231">
                  Contactformulier
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2232">
                  Een goed contactformulier verlaagt de drempel voor potentiële
                  klanten om contact op te nemen.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2233">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2234">
                <BookOpenIcon className="w-6 h-6 text-accent" data-id="element-2235" />
              </div>
              <div data-id="element-2236">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2237">Blog</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2238">
                  Een blog helpt je vindbaarheid in Google en positioneert je
                  als expert in jouw vakgebied.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2239">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2240">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2241" />
              </div>
              <div data-id="element-2242">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2243">SEO</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2244">
                  Zoekmachineoptimalisatie zorgt ervoor dat je website gevonden
                  wordt door je doelgroep.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2245">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2246">
                <LinkIcon className="w-6 h-6 text-accent" data-id="element-2247" />
              </div>
              <div data-id="element-2248">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2249">
                  Integraties
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2250">
                  Denk aan koppelingen met Google Analytics, social media,
                  boekingssystemen of CRM-software.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 4: Design */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2251">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2252" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2253">
          <div className="text-center mb-16" data-id="element-2254">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white font-bold text-sm rounded-full mb-4 border border-white/20" data-id="element-2255">
              Stap 4
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2256">
              Design: hoe ziet je website eruit?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2257">
              Een goed design combineert uitstraling met
              gebruiksvriendelijkheid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2258">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2259">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2260">
                <MonitorIcon className="w-6 h-6 text-white" data-id="element-2261" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2262">
                Gebruiksvriendelijkheid
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2263">
                Bezoekers moeten snel vinden wat ze zoeken. Een logische
                navigatie en duidelijke structuur zijn essentieel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2264">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2265">
                <SmartphoneIcon className="w-6 h-6 text-white" data-id="element-2266" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2267">
                Responsive design
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2268">
                Je website moet er perfect uitzien op desktop, tablet én mobiel.
                Meer dan 60% van het verkeer komt via mobiel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2269">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2270">
                <ZapIcon className="w-6 h-6 text-white" data-id="element-2271" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2272">
                Snelle laadtijd
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2273">
                Een snelle website scoort beter in Google en zorgt voor minder
                afhakers. Streef naar een laadtijd onder de 3 seconden.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 5: Livegang */}
      <section className="py-24 bg-white" data-id="element-2274">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2275">
          <div className="text-center mb-16" data-id="element-2276">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2277">
              Stap 5
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2278">
              Livegang: klaar voor lancering
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2279">
              De laatste stappen voordat je website live gaat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2280">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2281">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2282">
                <ServerIcon className="w-6 h-6 text-accent" data-id="element-2283" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2284">Hosting</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2285">
                Kies betrouwbare hosting met goede uptime en snelheid. Wij
                regelen dit volledig voor je.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2286">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2287">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2288" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2289">
                SSL-certificaat
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2290">
                Een SSL-certificaat (https) is verplicht voor veiligheid en
                wordt beloond door Google.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2291">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2292">
                <SettingsIcon className="w-6 h-6 text-accent" data-id="element-2293" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2294">
                SEO basis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2295">
                Zorg dat de technische SEO op orde is: meta-tags, sitemap,
                robots.txt en snelheidsoptimalisatie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2296">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2297">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2298">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2299">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2300">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2301">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2302">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2303">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2304">
              Kosten website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* Download Checklist */}
      <div className="py-16 bg-surface-muted" data-id="element-2305">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2306">
          <div className="bg-white rounded-2xl shadow-card p-8 lg:p-12 mb-16" data-id="element-2307">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-2308">
              Download de complete checklist (PDF)
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2309">
              Wil je deze checklist altijd bij de hand hebben? Download de
              uitgebreide PDF-versie inclusief handige templates en voorbeelden.
              Van strategie tot livegang, met deze checklist ben je optimaal
              voorbereid.
            </p>
            <a href="#/checklist-pdf" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2310">
              Download PDF
              <ArrowRightIcon className="w-5 h-5" data-id="element-2311" />
            </a>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2312">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2313">
          <div className="text-center mb-16" data-id="element-2314">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2315">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2316">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2317">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2318">
                  <span className="font-bold text-navy-900" data-id="element-2319">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2320" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2321" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2322">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2323" />
    </main>;
}